import { Link } from "react-router";
import { formatDateLong } from "@techplanner/shared/core";
import { useMe } from "../auth";
import { InterventionList } from "../components/domain";
import { Icon } from "../components/Icon";
import { Fab, Page, TopBar } from "../components/Layout";
import { Avatar, Empty, ErrorState, Section, Skeleton } from "../components/ui";
import { capitalize, plural } from "../lib/format";
import { useDashboard } from "../lib/queries";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5 || h >= 18) return "Bonsoir";
  return "Bonjour";
}

export function HomePage() {
  const me = useMe();
  const { data, error, isPending, refetch } = useDashboard();
  const firstName = me.displayName.split(" ")[0];

  return (
    <>
      <TopBar title="Accueil" large />
      <Page>
        <div className="hero">
          <div className="small muted">{data ? capitalize(formatDateLong(data.today)) : " "}
          </div>
          <h2 style={{ fontSize: "1.35rem", marginTop: 2 }}>
            {greeting()} {firstName}
          </h2>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <Icon name="shield" size={20} />
            <span className="small">
              Astreinte cette semaine :{" "}
              <strong>{data?.onCall.user ? data.onCall.user.displayName : "non renseignée"}</strong>
            </span>
            {data?.onCall.user && <span className="grow" />}
            {data?.onCall.user && <Avatar user={data.onCall.user} />}
          </div>
        </div>

        {error && !data && (
          <div className="section">
            <ErrorState error={error} onRetry={() => void refetch()} />
          </div>
        )}
        {isPending && (
          <div className="section">
            <Skeleton count={3} />
          </div>
        )}

        {data && (
          <>
            <div className="stat-grid section">
              <Link to="/maintenance" className={`stat ${data.overdue.length ? "alert-stat" : ""}`}>
                <strong>{data.overdue.length}</strong>
                <span>En retard</span>
              </Link>
              <Link to="/planning?unscheduled=1" className="stat">
                <strong>{data.unscheduled.length}</strong>
                <span>À planifier</span>
              </Link>
              {me.role === "admin" ? (
                <Link to="/invoicing" className="stat">
                  <strong>{data.toInvoice}</strong>
                  <span>À facturer</span>
                </Link>
              ) : (
                <Link to="/todos" className="stat">
                  <strong>{data.openTodos}</strong>
                  <span>Notes ouvertes</span>
                </Link>
              )}
            </div>

            {data.inProgress.length > 0 && (
              <Section title="En cours">
                <InterventionList items={data.inProgress} showDate />
              </Section>
            )}

            <Section
              title={`Mes interventions aujourd'hui${data.mine.today.length ? ` · ${data.mine.today.length}` : ""}`}
              action={
                <Link className="section-link" to="/planning">
                  Planning
                </Link>
              }
            >
              <InterventionList
                items={data.mine.today}
                empty={
                  <div className="card">
                    <Empty icon="calendar" title="Rien de prévu pour vous aujourd'hui">
                      Consultez le planning de l'équipe ou créez une intervention.
                    </Empty>
                  </div>
                }
              />
            </Section>

            {data.overdue.length > 0 && (
              <Section
                title={`En retard · ${data.overdue.length}`}
                action={
                  <Link className="section-link" to="/maintenance">
                    Tout voir
                  </Link>
                }
              >
                <InterventionList items={data.overdue.slice(0, 5)} showDate />
              </Section>
            )}

            {data.mine.upcoming.length > 0 && (
              <Section title="Mes prochaines interventions">
                <InterventionList items={data.mine.upcoming.slice(0, 6)} showDate />
              </Section>
            )}

            {data.unscheduled.length > 0 && (
              <Section title={`À planifier · ${plural(data.unscheduled.length, "demande")}`}>
                <InterventionList items={data.unscheduled.slice(0, 5)} />
              </Section>
            )}
          </>
        )}
      </Page>
      <Fab to="/interventions/new" label="Intervention" />
    </>
  );
}
